import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { MapPin, Calendar as CalendarIcon, Clock, User, Mail, Phone, Home, ArrowRight, ArrowLeft, Search, Car, Users, Briefcase, Check, CreditCard } from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const vehicleTypes = [
  { id: "saloon", name: "Saloon", description: "Standard comfortable ride", icon: "🚗", passengers: 4, luggage: 2 },
  { id: "estate", name: "Estate", description: "Extra space for luggage", icon: "🚙", passengers: 4, luggage: 4 },
  { id: "mpv", name: "MPV", description: "Group travel up to 7 passengers", icon: "🚐", passengers: 7, luggage: 5 },
  { id: "luxury", name: "Luxury", description: "Premium experience", icon: "✨", passengers: 4, luggage: 3 },
  { id: "executive", name: "Executive", description: "Business class travel", icon: "🎩", passengers: 4, luggage: 3 },
];

function NominatimAutocomplete({ placeholder, onPlaceSelect, value, icon }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSelected, setIsSelected] = useState(!!value);

  useEffect(() => { if (value) { setQuery(value); setIsSelected(true); } }, [value]);

  useEffect(() => {
    if (isSelected || query.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await axios.get('https://nominatim.openstreetmap.org/search', {
          params: { q: query, format: 'json', addressdetails: 1, countrycodes: 'gb', limit: 5 },
          headers: { 'User-Agent': 'RomanCars/1.0' }
        });
        setSuggestions(response.data);
        setShowSuggestions(true);
      } catch (error) { console.error('Geocoding error:', error); }
      setLoading(false);
    }, 500);
    return () => { clearTimeout(timer); setLoading(false); };
  }, [query, isSelected]);

  const handleSelect = (place) => {
    setQuery(place.display_name);
    setSuggestions([]);
    setShowSuggestions(false);
    setIsSelected(true);
    onPlaceSelect({ address: place.display_name, lat: parseFloat(place.lat), lng: parseFloat(place.lon) });
    toast.success('Location selected!');
  };

  return (
    <div className="relative w-full">
      <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border-2 border-gray-200 focus-within:border-orange-500 transition-colors">
        {icon}
        <Input placeholder={placeholder} value={query} onChange={(e) => { setQuery(e.target.value); setIsSelected(false); }} onFocus={() => { if (suggestions.length > 0 && !isSelected) setShowSuggestions(true); }} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} className="border-0 bg-transparent focus-visible:ring-0 text-base" />
        {loading && <Search size={16} className="animate-spin text-gray-400" />}
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden" onMouseDown={(e) => e.preventDefault()}>
          {suggestions.map((place, index) => (
            <div key={index} className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 cursor-pointer transition-colors" onMouseDown={(e) => { e.preventDefault(); handleSelect(place); }}>
              <MapPin size={14} className="text-orange-500 flex-shrink-0" />
              <span className="text-sm text-gray-700 line-clamp-1">{place.display_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function UserDashboard({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [pickupLocation, setPickupLocation] = useState({ address: "", lat: null, lng: null });
  const [dropoffLocation, setDropoffLocation] = useState({ address: "", lat: null, lng: null });
  const [prices, setPrices] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedTripType, setSelectedTripType] = useState(null);
  const [tripDiscounts, setTripDiscounts] = useState({ one_way: { type: "amount", value: 0, enabled: false }, round_trip: { type: "amount", value: 0, enabled: false } });
  const [bookingDate, setBookingDate] = useState(new Date());
  const [bookingTime, setBookingTime] = useState("09:00");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [flightNumber, setFlightNumber] = useState("");

  useEffect(() => {
    const initFromState = async () => {
      // Check URL params first
      const params = new URLSearchParams(window.location.search);
      const pickupAddr = params.get("pickup");
      const plat = params.get("plat");
      const plng = params.get("plng");
      const dropoffAddr = params.get("dropoff");
      const dlat = params.get("dlat");
      const dlng = params.get("dlng");
      
      if (pickupAddr && plat && plng && dropoffAddr && dlat && dlng) {
        const pickup = { address: pickupAddr, lat: parseFloat(plat), lng: parseFloat(plng) };
        const dropoff = { address: dropoffAddr, lat: parseFloat(dlat), lng: parseFloat(dlng) };
        setPickupLocation(pickup);
        setDropoffLocation(dropoff);
        setLoading(true);
        try {
          const calculatedPrices = {};
          for (const vehicle of vehicleTypes) {
            calculatedPrices[vehicle.id] = {};
            for (const tripType of ["one_way", "round_trip"]) {
              const response = await axios.post(`${API}/calculate-price`, {
                pickup_location: { lat: pickup.lat, lng: pickup.lng, address: pickup.address },
                dropoff_location: { lat: dropoff.lat, lng: dropoff.lng, address: dropoff.address },
                vehicle_type: vehicle.id,
                trip_type: tripType,
              });
              calculatedPrices[vehicle.id][tripType] = response.data.price;
            }
          }
          setPrices(calculatedPrices);
          setStep(2);
          toast.success("Select your vehicle!");
          try {
            const discountResponse = await axios.get(`${API}/trip-discounts`);
            if (discountResponse.data.success) setTripDiscounts(discountResponse.data.discounts);
          } catch (error) { console.log("No trip discounts"); }
          window.history.replaceState({}, document.title, "/book");
        } catch (error) {
          console.error("Failed to calculate prices:", error);
        } finally { setLoading(false); }
        return;
      }
      
      if (location.state && location.state.pickupLocation && location.state.dropoffLocation) {
        const pickup = location.state.pickupLocation;
        const dropoff = location.state.dropoffLocation;
        setPickupLocation(pickup);
        setDropoffLocation(dropoff);
        if (pickup.lat && dropoff.lat) {
          setLoading(true);
          try {
            const calculatedPrices = {};
            for (const vehicle of vehicleTypes) {
              calculatedPrices[vehicle.id] = {};
              for (const tripType of ["one_way", "round_trip"]) {
                const response = await axios.post(`${API}/calculate-price`, {
                  pickup_location: { lat: pickup.lat, lng: pickup.lng, address: pickup.address },
                  dropoff_location: { lat: dropoff.lat, lng: dropoff.lng, address: dropoff.address },
                  vehicle_type: vehicle.id,
                  trip_type: tripType,
                });
                calculatedPrices[vehicle.id][tripType] = response.data.price;
              }
            }
            setPrices(calculatedPrices);
            setStep(2);
            toast.success("Prices calculated! Select your vehicle.");
            try {
              const discountResponse = await axios.get(`${API}/trip-discounts`);
              if (discountResponse.data.success) setTripDiscounts(discountResponse.data.discounts);
            } catch (error) { console.log("No trip discounts"); }
          } catch (error) {
            console.error("Failed to calculate prices:", error);
            toast.error("Failed to calculate prices.");
          } finally { setLoading(false); }
        }
      }
    };
    initFromState();
  }, [location.state]);

  const handleStep1Next = async () => {
    if (!pickupLocation.address || !dropoffLocation.address) { toast.error("Please select both locations"); return; }
    if (!pickupLocation.lat || !dropoffLocation.lat) { toast.error("Please select from dropdown"); return; }
    setLoading(true);
    try {
      const calculatedPrices = {};
      for (const vehicle of vehicleTypes) {
        calculatedPrices[vehicle.id] = {};
        for (const tripType of ["one_way", "round_trip"]) {
          const response = await axios.post(`${API}/calculate-price`, {
            pickup_location: { lat: pickupLocation.lat, lng: pickupLocation.lng, address: pickupLocation.address },
            dropoff_location: { lat: dropoffLocation.lat, lng: dropoffLocation.lng, address: dropoffLocation.address },
            vehicle_type: vehicle.id,
            trip_type: tripType,
          });
          calculatedPrices[vehicle.id][tripType] = response.data.price;
        }
      }
      setPrices(calculatedPrices);
      setStep(2);
      toast.success("Prices calculated!");
      try {
        const discountResponse = await axios.get(`${API}/trip-discounts`);
        if (discountResponse.data.success) setTripDiscounts(discountResponse.data.discounts);
      } catch (error) { console.log("No trip discounts"); }
    } catch (error) { toast.error("Failed to calculate prices"); }
    finally { setLoading(false); }
  };

  const calculateTripDiscount = (price, tripType) => {
    const discount = tripDiscounts[tripType];
    if (!discount || !discount.enabled || discount.value <= 0) return 0;
    if (discount.type === 'percentage') return Math.min((price * discount.value) / 100, price);
    return Math.min(discount.value, price);
  };

  const getDisplayPrice = (vehicleId, tripType) => {
    const originalPrice = prices[vehicleId][tripType];
    const discountAmount = calculateTripDiscount(originalPrice, tripType);
    const finalPrice = Math.max(0, originalPrice - discountAmount);
    const discountPercentage = originalPrice > 0 ? Math.round((discountAmount / originalPrice) * 100) : 0;
    return { original: originalPrice, discount: discountAmount, discountPercentage, final: finalPrice, hasDiscount: discountAmount > 0 && discountAmount < originalPrice };
  };

  const handleStep2Next = () => {
    if (!selectedVehicle || !selectedTripType) { toast.error("Please select a vehicle and trip type"); return; }
    setStep(3);
  };

  const handleBookRide = async () => {
    if (!customerName.trim() || !customerPhone.trim() || !customerEmail.trim() || !customerAddress.trim()) { toast.error("Please fill all details"); return; }
    setLoading(true);
    try {
      const priceInfo = getDisplayPrice(selectedVehicle, selectedTripType);
      const bookingData = {
        customer_name: customerName, 
        customer_phone: customerPhone, 
        customer_email: customerEmail, 
        customer_address: customerAddress,
        flight_number: flightNumber,
        pickup_location: { lat: pickupLocation.lat, lng: pickupLocation.lng, address: pickupLocation.address },
        dropoff_location: { lat: dropoffLocation.lat, lng: dropoffLocation.lng, address: dropoffLocation.address },
        vehicle_type: selectedVehicle, 
        trip_type: selectedTripType, 
        booking_date: format(bookingDate, "yyyy-MM-dd"), 
        booking_time: bookingTime, 
        price: priceInfo.final,
        payment_status: "pending"
      };
      if (priceInfo.hasDiscount) { 
        bookingData.discount_amount = priceInfo.discount; 
        bookingData.original_price = priceInfo.original; 
      }
      
      // Step 1: Create the booking first
      const bookingResponse = await axios.post(`${API}/bookings?user_id=demo-user-123`, bookingData);
      const booking = bookingResponse.data.booking;
      
      toast.success("Booking created! Redirecting to payment...");
      
      // Step 2: Create Stripe checkout session
      try {
        const paymentResponse = await axios.post(`${API}/payments/checkout`, {
          booking_id: booking.id,
          amount: priceInfo.final,
          customer_email: customerEmail,
          customer_name: customerName,
          description: `${vehicleTypes.find(v => v.id === selectedVehicle)?.name} - ${selectedTripType === "one_way" ? "One Way" : "Round Trip"}`,
          success_url: `${window.location.origin}/booking-success?session_id={CHECKOUT_SESSION_ID}&booking_id=${booking.id}`,
          cancel_url: `${window.location.origin}/booking-cancel?booking_id=${booking.id}`
        });
        
        // Redirect to Stripe Checkout
        if (paymentResponse.data.checkout_url) {
          window.location.href = paymentResponse.data.checkout_url;
        } else if (paymentResponse.data.url) {
          window.location.href = paymentResponse.data.url;
        } else {
          throw new Error("No checkout URL returned");
        }
      } catch (paymentError) {
        console.error("Payment error:", paymentError);
        toast.error("Payment setup failed. Please try again or contact support.");
        // Still redirect to tracking if payment fails
        setTimeout(() => { navigate(`/track/${booking.id}`); }, 2000);
      }
    } catch (error) { 
      console.error("Booking error:", error);
      toast.error("Failed to create booking."); 
      setLoading(false); 
    }
  };

  const handleReset = () => {
    setStep(1); setPickupLocation({ address: "", lat: null, lng: null }); setDropoffLocation({ address: "", lat: null, lng: null });
    setPrices(null); setSelectedVehicle(null); setSelectedTripType(null); setBookingDate(new Date()); setBookingTime("09:00");
    setCustomerName(""); setCustomerPhone(""); setCustomerEmail(""); setCustomerAddress("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
              <Car size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Roman Cars</h1>
              <p className="text-slate-400 text-xs">Premium Taxi Service</p>
            </div>
          </div>
          {/* Progress Steps */}
          <div className="hidden md:flex items-center gap-2">
            {[{n:1,t:'Location'},{n:2,t:'Vehicle'},{n:3,t:'Payment'}].map((s,i) => (
              <React.Fragment key={s.n}>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${step >= s.n ? 'bg-orange-500 text-white' : 'bg-white/10 text-slate-400'}`}>
                  <span className="w-5 h-5 flex items-center justify-center text-xs font-bold">{step > s.n ? <Check size={14}/> : s.n}</span>
                  <span className="text-sm font-medium">{s.t}</span>
                </div>
                {i < 2 && <div className={`w-8 h-0.5 ${step > s.n ? 'bg-orange-500' : 'bg-white/20'}`}/>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Step 1: Location */}
        {step === 1 && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Where are you going?</h2>
              <p className="text-slate-400">Enter your pickup and drop-off locations</p>
            </div>
            <Card className="bg-white/95 backdrop-blur shadow-2xl border-0 rounded-2xl">
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label className="text-gray-700 font-medium mb-2 block">Pickup Location</Label>
                  <NominatimAutocomplete placeholder="Enter pickup address" value={pickupLocation.address} onPlaceSelect={setPickupLocation} icon={<MapPin size={20} className="text-green-500 flex-shrink-0" />} />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium mb-2 block">Drop-off Location</Label>
                  <NominatimAutocomplete placeholder="Enter destination" value={dropoffLocation.address} onPlaceSelect={setDropoffLocation} icon={<MapPin size={20} className="text-red-500 flex-shrink-0" />} />
                </div>
                <Button onClick={handleStep1Next} disabled={loading} className="w-full h-12 text-lg font-semibold bg-orange-500 hover:bg-orange-600 rounded-xl mt-2">
                  {loading ? "Calculating..." : "Get Prices"} <ArrowRight size={20} className="ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Vehicle Selection */}
        {step === 2 && prices && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Choose Your Ride</h2>
              <p className="text-slate-400">
                <MapPin size={14} className="inline mr-1" />
                {pickupLocation.address?.split(',')[0]} → {dropoffLocation.address?.split(',')[0]}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {vehicleTypes.map((vehicle) => {
                const oneWay = getDisplayPrice(vehicle.id, "one_way");
                const roundTrip = getDisplayPrice(vehicle.id, "round_trip");
                const isSelected = selectedVehicle === vehicle.id;

                return (
                  <div
                    key={vehicle.id}
                    className={`bg-white rounded-2xl p-5 transition-all cursor-pointer ${isSelected ? 'ring-2 ring-orange-500 shadow-lg shadow-orange-500/20' : 'hover:shadow-lg'}`}
                  >
                    {/* Vehicle Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center text-3xl">
                          {vehicle.icon}
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-gray-900">{vehicle.name}</h3>
                          <p className="text-gray-500 text-sm">{vehicle.description}</p>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                          <Check size={14} className="text-white" />
                        </div>
                      )}
                    </div>

                    {/* Vehicle Features */}
                    <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1"><Users size={16} /> {vehicle.passengers}</span>
                      <span className="flex items-center gap-1"><Briefcase size={16} /> {vehicle.luggage}</span>
                    </div>

                    {/* Price Options */}
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => { setSelectedVehicle(vehicle.id); setSelectedTripType("one_way"); }}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${isSelected && selectedTripType === "one_way" ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}`}
                      >
                        <span className="text-xs text-gray-500 block mb-1">One Way</span>
                        {oneWay.hasDiscount ? (
                          <>
                            <span className="text-gray-400 line-through text-xs">£{oneWay.original.toFixed(2)}</span>
                            <span className="text-xl font-bold text-orange-600 block">£{oneWay.final.toFixed(2)}</span>
                          </>
                        ) : (
                          <span className="text-xl font-bold text-gray-900">£{oneWay.original.toFixed(2)}</span>
                        )}
                      </button>
                      <button
                        onClick={() => { setSelectedVehicle(vehicle.id); setSelectedTripType("round_trip"); }}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${isSelected && selectedTripType === "round_trip" ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}`}
                      >
                        <span className="text-xs text-gray-500 block mb-1">Round Trip</span>
                        {roundTrip.hasDiscount ? (
                          <>
                            <span className="text-gray-400 line-through text-xs">£{roundTrip.original.toFixed(2)}</span>
                            <span className="text-xl font-bold text-orange-600 block">£{roundTrip.final.toFixed(2)}</span>
                          </>
                        ) : (
                          <span className="text-xl font-bold text-gray-900">£{roundTrip.original.toFixed(2)}</span>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Navigation Buttons */}
            <div className="flex gap-3 max-w-xl mx-auto">
              <Button variant="outline" onClick={() => setStep(1)} className="h-12 px-6 rounded-xl bg-white/10 border-white/20 text-white hover:bg-white/20">
                <ArrowLeft size={18} className="mr-2" /> Back
              </Button>
              <Button onClick={handleStep2Next} disabled={!selectedVehicle || !selectedTripType} className="flex-1 h-12 text-lg font-semibold bg-orange-500 hover:bg-orange-600 rounded-xl">
                Continue <ArrowRight size={20} className="ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Customer Details & Payment */}
        {step === 3 && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Complete Your Booking</h2>
              <p className="text-slate-400">Enter your details and proceed to payment</p>
            </div>

            <Card className="bg-white/95 backdrop-blur shadow-2xl border-0 rounded-2xl mb-4">
              <CardContent className="p-6">
                {/* Booking Summary */}
                <div className="bg-orange-50 rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Vehicle</span>
                    <span className="font-semibold text-gray-900">{vehicleTypes.find(v => v.id === selectedVehicle)?.name} - {selectedTripType === "one_way" ? "One Way" : "Round Trip"}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total</span>
                    <span className="text-2xl font-bold text-orange-600">£{getDisplayPrice(selectedVehicle, selectedTripType).final.toFixed(2)}</span>
                  </div>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <Label className="text-gray-700 font-medium mb-2 block">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-12 justify-start rounded-xl border-2">
                          <CalendarIcon size={18} className="mr-2 text-gray-500" />
                          {format(bookingDate, "PPP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={bookingDate} onSelect={setBookingDate} disabled={(date) => date < new Date()} /></PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium mb-2 block">Time</Label>
                    <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 h-12 border-2 border-gray-200">
                      <Clock size={18} className="text-gray-500" />
                      <Input type="time" value={bookingTime} onChange={(e) => setBookingTime(e.target.value)} className="border-0 bg-transparent focus-visible:ring-0" />
                    </div>
                  </div>
                </div>

                {/* Customer Info */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-700 font-medium mb-2 block">Full Name</Label>
                    <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border-2 border-gray-200 focus-within:border-orange-500">
                      <User size={18} className="text-gray-500" />
                      <Input placeholder="Enter your full name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="border-0 bg-transparent focus-visible:ring-0" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium mb-2 block">Phone Number</Label>
                    <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border-2 border-gray-200 focus-within:border-orange-500">
                      <Phone size={18} className="text-gray-500" />
                      <Input type="tel" placeholder="+44 7XXX XXXXXX" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="border-0 bg-transparent focus-visible:ring-0" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium mb-2 block">Email</Label>
                    <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border-2 border-gray-200 focus-within:border-orange-500">
                      <Mail size={18} className="text-gray-500" />
                      <Input type="email" placeholder="your@email.com" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className="border-0 bg-transparent focus-visible:ring-0" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium mb-2 block">Address</Label>
                    <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border-2 border-gray-200 focus-within:border-orange-500">
                      <Home size={18} className="text-gray-500" />
                      <Input placeholder="Enter your address" value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} className="border-0 bg-transparent focus-visible:ring-0" />
                    </div>
                  </div>
                </div>

            {/* Flight Number */}
            <div className="mb-4">
              <Label className="text-gray-700 font-medium mb-2 block">Flight Number (Optional)</Label>
              <div className="relative">
                <input placeholder="Enter flight number" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} className="border-0 bg-transparent focus-visible:ring-0 w-full p-3 pl-10 border border-gray-200 rounded-xl" />
              </div>
            </div>

                {/* Payment Info */}
                <div className="mt-6 p-4 bg-slate-100 rounded-xl flex items-center gap-3">
                  <CreditCard size={24} className="text-slate-600" />
                  <div>
                    <p className="font-medium text-slate-800">Secure Payment via Stripe</p>
                    <p className="text-sm text-slate-500">You'll be redirected to complete payment</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Navigation Buttons */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="h-12 px-6 rounded-xl bg-white/10 border-white/20 text-white hover:bg-white/20">
                <ArrowLeft size={18} className="mr-2" /> Back
              </Button>
              <Button onClick={handleBookRide} disabled={loading} className="flex-1 h-14 text-lg font-semibold bg-orange-500 hover:bg-orange-600 rounded-xl">
                {loading ? "Processing..." : <><CreditCard size={20} className="mr-2" /> Pay & Confirm Booking</>}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
